import { makeAutoObservable } from 'mobx';

import { ApiRoutes, HttpMethod } from '../config/ApiRoutes';
import type { RootStore } from '.';
import type {
  AddMotorClubDriverMembershipRequest,
  AddMotorClubDriverMembershipResponse,
  AddMotorClubTrafficIncidentRequest,
  AddMotorClubTrafficIncidentResponse,
  AddMotorClubUnitMembershipRequest,
  AddMotorClubUnitMembershipResponse,
  MotorClubMembershipDetailsResponse,
  MotorClubMembershipDriver,
  MotorClubRoadsideUnit,
  MotorClubTrafficIncident,
  MotorClubTrafficIncidentDriversLookupResponse,
  MotorClubTrafficIncidentsResponse,
  TerminateMotorClubMembershipRequest,
  TerminateMotorClubMembershipResponse,
} from './api/motorClubMembership.api';

export class MotorClubMembershipStore {
  private _isLoading = false;
  private _isMembershipDriversLoadingMore = false;
  private _isTrafficIncidentsLoading = false;
  private _isTrafficIncidentsLoadingMore = false;
  private _isRoadsideUnitsLoading = false;
  private _isRoadsideUnitsLoadingMore = false;
  private _isMembershipDetailsLoading = false;
  private _membershipDetails: MotorClubMembershipDetailsResponse | null = null;
  private _membershipDrivers: MotorClubMembershipDriver[] = [];
  private _membershipDriversNextLink = '';
  private _membershipDriversTotal = 0;
  private _trafficIncidents: MotorClubTrafficIncident[] = [];
  private _trafficIncidentsNextLink = '';
  private _trafficIncidentsTotal = 0;
  private _roadsideUnits: MotorClubRoadsideUnit[] = [];
  private _roadsideUnitsNextLink = '';
  private _roadsideUnitsHasMore = false;

  constructor(public rootStore: RootStore) {
    makeAutoObservable(this, { rootStore: false });
  }

  setLoading(value: boolean) {
    this._isLoading = value;
  }

  setMembershipDrivers(value: MotorClubMembershipDriver[]) {
    this._membershipDrivers = value;
  }

  setMembershipDetails(value: MotorClubMembershipDetailsResponse | null) {
    this._membershipDetails = value;
  }

  setMembershipDetailsLoading(value: boolean) {
    this._isMembershipDetailsLoading = value;
  }

  setMembershipDriversWithMode(
    value: MotorClubMembershipDriver[],
    append = false,
  ) {
    if (!append) {
      this._membershipDrivers = value;
      return;
    }

    const nextByMemberId = new Map<string, MotorClubMembershipDriver>();
    this._membershipDrivers.forEach((item) => {
      nextByMemberId.set(item.memberId, item);
    });
    value.forEach((item) => {
      nextByMemberId.set(item.memberId, item);
    });
    this._membershipDrivers = Array.from(nextByMemberId.values());
  }

  setMembershipDriversLoadingMore(value: boolean) {
    this._isMembershipDriversLoadingMore = value;
  }

  setMembershipDriversNextLink(value: string) {
    this._membershipDriversNextLink = value;
  }

  setMembershipDriversTotal(value: number) {
    this._membershipDriversTotal = value;
  }

  setTrafficIncidents(value: MotorClubTrafficIncident[], append = false) {
    if (!append) {
      this._trafficIncidents = value;
      return;
    }

    const nextByIncidentNumber = new Map<string, MotorClubTrafficIncident>();
    this._trafficIncidents.forEach((item) => {
      nextByIncidentNumber.set(item.incidentId, item);
    });
    value.forEach((item) => {
      nextByIncidentNumber.set(item.incidentId, item);
    });
    this._trafficIncidents = Array.from(nextByIncidentNumber.values());
  }

  setTrafficIncidentsLoading(value: boolean) {
    this._isTrafficIncidentsLoading = value;
  }

  setTrafficIncidentsLoadingMore(value: boolean) {
    this._isTrafficIncidentsLoadingMore = value;
  }

  setTrafficIncidentsNextLink(value: string) {
    this._trafficIncidentsNextLink = value;
  }

  setTrafficIncidentsTotal(value: number) {
    this._trafficIncidentsTotal = value;
  }

  setRoadsideUnits(
    value: MotorClubRoadsideUnit[],
    hasMore: boolean,
    append = false,
  ) {
    if (!append) {
      this._roadsideUnits = value;
      this._roadsideUnitsHasMore = hasMore;
      return;
    }

    const nextByUnit = new Map<string, MotorClubRoadsideUnit>();
    this._roadsideUnits.forEach((item) => {
      nextByUnit.set(item.unitNumber, item);
    });
    value.forEach((item) => {
      nextByUnit.set(item.unitNumber, item);
    });
    this._roadsideUnits = Array.from(nextByUnit.values());
    this._roadsideUnitsHasMore = hasMore;
  }

  setRoadsideUnitsLoading(value: boolean) {
    this._isRoadsideUnitsLoading = value;
  }

  setRoadsideUnitsLoadingMore(value: boolean) {
    this._isRoadsideUnitsLoadingMore = value;
  }

  setRoadsideUnitsNextLink(value: string) {
    this._roadsideUnitsNextLink = value;
  }

  setRoadsideUnitsHasMore(value: boolean) {
    this._roadsideUnitsHasMore = value;
  }

  get isLoading() {
    return this._isLoading;
  }

  get isRoadsideUnitsLoading() {
    return this._isRoadsideUnitsLoading;
  }

  get isRoadsideUnitsLoadingMore() {
    return this._isRoadsideUnitsLoadingMore;
  }

  get isTrafficIncidentsLoading() {
    return this._isTrafficIncidentsLoading;
  }

  get isTrafficIncidentsLoadingMore() {
    return this._isTrafficIncidentsLoadingMore;
  }

  get isMembershipDriversLoadingMore() {
    return this._isMembershipDriversLoadingMore;
  }

  get isMembershipDetailsLoading() {
    return this._isMembershipDetailsLoading;
  }

  get MembershipDetails() {
    return this._membershipDetails;
  }

  get MembershipDrivers() {
    return this._membershipDrivers;
  }

  get MembershipDriversCount() {
    return this._membershipDrivers.length;
  }

  get MembershipDriversNextLink() {
    return this._membershipDriversNextLink;
  }

  get MembershipDriversHasMore() {
    if (!this._membershipDriversTotal) {
      return false;
    }

    return (
      !!this._membershipDriversNextLink ||
      this._membershipDrivers.length < this._membershipDriversTotal
    );
  }

  get TrafficIncidents() {
    return this._trafficIncidents;
  }

  get TrafficIncidentsCount() {
    return this._trafficIncidents.length;
  }

  get TrafficIncidentsNextLink() {
    return this._trafficIncidentsNextLink;
  }

  get TrafficIncidentsHasMore() {
    if (!this._trafficIncidentsTotal) {
      return false;
    }

    return !!this._trafficIncidentsNextLink;
  }

  get RoadsideUnits() {
    return this._roadsideUnits;
  }

  get RoadsideUnitsCount() {
    return this._roadsideUnits.length;
  }

  get RoadsideUnitsNextLink() {
    return this._roadsideUnitsNextLink;
  }

  get RoadsideUnitsHasMore() {
    return this._roadsideUnitsHasMore;
  }

  getMembershipDrivers = async ({
    append = false,
    limit = 20,
    terminated = false,
  }: {
    append?: boolean;
    limit?: number;
    terminated?: boolean;
  } = {}) => {
    if (append && this._isMembershipDriversLoadingMore) {
      return;
    }

    if (append && !this.MembershipDriversHasMore) {
      return;
    }

    if (append) {
      this.setMembershipDriversLoadingMore(true);
    } else {
      this.setLoading(true);
      this.setMembershipDriversNextLink('');
      this.setMembershipDriversTotal(0);
      this.setMembershipDrivers([]);
    }

    const responseOrErr =
      await this.rootStore.apiStore.motorClubMembershipApiStore.getMemberships({
        limit,
        nextLink: append
          ? this._membershipDriversNextLink || undefined
          : undefined,
        terminated,
      });

    if (append) {
      this.setMembershipDriversLoadingMore(false);
    } else {
      this.setLoading(false);
    }

    if (responseOrErr.isOk()) {
      const items = responseOrErr.value.items || [];
      const total = responseOrErr.value.total || 0;
      const nextLink = responseOrErr.value.nextLink || '';

      this.setMembershipDriversWithMode(items, append);
      this.setMembershipDriversTotal(total);
      this.setMembershipDriversNextLink(nextLink);
    }

    return responseOrErr;
  };

  getMembershipDetails = async () => {
    this.setMembershipDetailsLoading(true);

    const detailsGetter = (
      this.rootStore.apiStore.motorClubMembershipApiStore as {
        getMembershipDetails?: (() => Promise<any>) | undefined;
      }
    ).getMembershipDetails;

    const responseOrErr =
      typeof detailsGetter === 'function'
        ? await detailsGetter.call(
            this.rootStore.apiStore.motorClubMembershipApiStore,
          )
        : await this.rootStore.apiStore.call<MotorClubMembershipDetailsResponse>(
            {
              method: HttpMethod.GET,
              url: ApiRoutes.MotorClubs.Details.Endpoint,
            },
          );

    this.setMembershipDetailsLoading(false);

    if (responseOrErr.isOk()) {
      this.setMembershipDetails(responseOrErr.value);
    }

    return responseOrErr;
  };

  getRoadsideUnits = async ({
    append = false,
    limit = 20,
    terminated = false,
  }: {
    append?: boolean;
    limit?: number;
    terminated?: boolean;
  } = {}) => {
    if (append && this._isRoadsideUnitsLoadingMore) {
      return;
    }

    if (append && !this.RoadsideUnitsHasMore) {
      return;
    }

    if (append) {
      this.setRoadsideUnitsLoadingMore(true);
    } else {
      this.setRoadsideUnitsLoading(true);
      this.setRoadsideUnitsNextLink('');
      this.setRoadsideUnits([], false);
    }

    const responseOrErr =
      await this.rootStore.apiStore.motorClubMembershipApiStore.getRoadsideUnits(
        {
          limit,
          nextLink: append
            ? this._roadsideUnitsNextLink || undefined
            : undefined,
          terminated,
        },
      );

    if (append) {
      this.setRoadsideUnitsLoadingMore(false);
    } else {
      this.setRoadsideUnitsLoading(false);
    }

    if (responseOrErr.isOk()) {
      const items = responseOrErr.value.items || [];
      const nextLink = responseOrErr.value.nextLink || '';
      const hasMore = responseOrErr.value.hasMore || false;

      this.setRoadsideUnits(items, hasMore, append);
      this.setRoadsideUnitsNextLink(nextLink);
    }

    return responseOrErr;
  };

  getTrafficIncidents = async ({
    append = false,
    limit = 20,
  }: {
    append?: boolean;
    limit?: number;
  } = {}) => {
    if (append && this._isTrafficIncidentsLoadingMore) {
      return;
    }

    if (append && !this.TrafficIncidentsHasMore) {
      return;
    }

    if (append) {
      this.setTrafficIncidentsLoadingMore(true);
    } else {
      this.setTrafficIncidentsLoading(true);
      this.setTrafficIncidentsNextLink('');
      this.setTrafficIncidentsTotal(0);
      this.setTrafficIncidents([]);
    }

    const responseOrErr =
      await this.rootStore.apiStore.motorClubMembershipApiStore.getTrafficIncidents(
        {
          limit,
          nextLink: append
            ? this._trafficIncidentsNextLink || undefined
            : undefined,
        },
      );

    if (append) {
      this.setTrafficIncidentsLoadingMore(false);
    } else {
      this.setTrafficIncidentsLoading(false);
    }

    if (responseOrErr.isOk()) {
      const items = responseOrErr.value.items || [];
      const total = responseOrErr.value.total || 0;
      const nextLink = responseOrErr.value.nextLink || '';

      this.setTrafficIncidents(items, append);
      this.setTrafficIncidentsTotal(total);
      this.setTrafficIncidentsNextLink(nextLink);
    }

    return responseOrErr;
  };

  addMembershipUnit = async (payload: AddMotorClubUnitMembershipRequest) => {
    const apiStore = this.rootStore.apiStore.motorClubMembershipApiStore as {
      addUnitMembership?: (
        data: AddMotorClubUnitMembershipRequest,
      ) => Promise<any>;
    };

    if (typeof apiStore.addUnitMembership === 'function') {
      return apiStore.addUnitMembership(payload);
    }

    // Fallback for stale runtime instances during HMR where method was not yet attached.
    return this.rootStore.apiStore.call<AddMotorClubUnitMembershipResponse>({
      data: payload,
      method: HttpMethod.POST,
      url: ApiRoutes.MotorClubs.AddUnitMembership.Endpoint,
    });
  };

  addMembershipDriver = async (
    payload: AddMotorClubDriverMembershipRequest,
  ) => {
    const apiStore = this.rootStore.apiStore.motorClubMembershipApiStore as {
      addDriverMembership?: (
        data: AddMotorClubDriverMembershipRequest,
      ) => Promise<any>;
    };

    if (typeof apiStore.addDriverMembership === 'function') {
      return apiStore.addDriverMembership(payload);
    }

    // Fallback for stale runtime instances during HMR where method was not yet attached.
    return this.rootStore.apiStore.call<AddMotorClubDriverMembershipResponse>({
      data: payload,
      method: HttpMethod.POST,
      url: ApiRoutes.MotorClubs.AddDriverMembership.Endpoint,
    });
  };

  getTrafficIncidentDriversLookup = async () => {
    const lookupGetter = (
      this.rootStore.apiStore.motorClubMembershipApiStore as {
        getTrafficIncidentDriversLookup?: (() => Promise<any>) | undefined;
      }
    ).getTrafficIncidentDriversLookup;

    if (typeof lookupGetter === 'function') {
      return lookupGetter.call(
        this.rootStore.apiStore.motorClubMembershipApiStore,
      );
    }

    return this.rootStore.apiStore.call<MotorClubTrafficIncidentDriversLookupResponse>(
      {
        method: HttpMethod.GET,
        url: ApiRoutes.MotorClubs.TrafficIncidentDriversLookup.Endpoint,
      },
    );
  };

  getTrafficIncidentSubIncidents = (incidentId: string) => {
    const apiStore = this.rootStore.apiStore.motorClubMembershipApiStore as {
      getTrafficIncidentSubIncidents?: (id: string) => Promise<any>;
    };

    if (typeof apiStore.getTrafficIncidentSubIncidents === 'function') {
      return apiStore.getTrafficIncidentSubIncidents(incidentId);
    }

    return this.rootStore.apiStore.call<MotorClubTrafficIncidentsResponse>({
      method: HttpMethod.GET,
      url: ApiRoutes.MotorClubs.TrafficIncidentSubIncidents.Endpoint(
        incidentId,
      ),
    });
  };

  addTrafficIncident = async (payload: AddMotorClubTrafficIncidentRequest) => {
    const apiStore = this.rootStore.apiStore.motorClubMembershipApiStore as {
      addTrafficIncident?: (
        data: AddMotorClubTrafficIncidentRequest,
      ) => Promise<any>;
    };

    if (typeof apiStore.addTrafficIncident === 'function') {
      return apiStore.addTrafficIncident(payload);
    }

    return this.rootStore.apiStore.call<AddMotorClubTrafficIncidentResponse>({
      data: payload,
      method: HttpMethod.POST,
      url: ApiRoutes.MotorClubs.AddTrafficIncident.Endpoint,
    });
  };

  terminateMembership = async (
    payload: TerminateMotorClubMembershipRequest,
  ) => {
    const apiStore = this.rootStore.apiStore.motorClubMembershipApiStore as {
      terminateMembership?: (
        data: TerminateMotorClubMembershipRequest,
      ) => Promise<any>;
    };

    if (typeof apiStore.terminateMembership === 'function') {
      return apiStore.terminateMembership(payload);
    }

    return this.rootStore.apiStore.call<TerminateMotorClubMembershipResponse>({
      data: payload,
      method: HttpMethod.POST,
      url: ApiRoutes.MotorClubs.TerminateMembership.Endpoint,
    });
  };
}
